import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { BuildingOffice2Icon } from "@heroicons/react/24/outline";
import { AppDispatch, RootState } from "../../../store/store";
import { STATUS } from "../../../store/slices/OrganizationSlice";
import {
  clearDeactivateError,
  DeactivateResult,
  OrganizationV3,
  resetDeactivationState,
} from "../../../store/slices/OrganizationsV3Slice";
import {
  deactivateOrganizationAsync,
  fetchDeactivationPreviewAsync,
} from "../../../store/thunks/OrganizationsV3Thunk";
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from "../../Catalyst/dialog";
import { Checkbox, CheckboxField, CheckboxGroup } from "../../Catalyst/checkbox";
import { Label } from "../../Catalyst/fieldset";
import { Button } from "../../Catalyst/button";
import { useToast } from "../../../contexts/ToastContext";

const ButtonSpinner = () => (
  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
);

// "(3 sub-orgs)" — the grey count shown after each cascade label. Omitted while the
// preview is loading or unavailable so the label never reads "(undefined users)".
const CountHint = ({ count, noun }: { count: number | undefined; noun: string }) =>
  typeof count === "number" ? (
    <span className="text-textColor">
      {" "}
      ({count} {noun})
    </span>
  ) : null;

const pluralize = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

interface DeactivateOrgDialogProps {
  organization: OrganizationV3 | null;
  open: boolean;
  onClose: () => void;
  // Called after a successful deactivation so the parent refetches the list and stats.
  onDeactivated: () => void;
}

const DeactivateOrgDialog = ({
  organization,
  open,
  onClose,
  onDeactivated,
}: DeactivateOrgDialogProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSuccessToast } = useToast();
  const { deactivationPreview, previewStatus, previewError, deactivateStatus, deactivateError } =
    useSelector((state: RootState) => state.OrganizationsV3Store);

  // Per the ticket: the two cascades default on, sessions and email default off.
  const [cascadeSubOrgs, setCascadeSubOrgs] = useState(true);
  const [cascadeUsers, setCascadeUsers] = useState(true);
  const [revokeSessions, setRevokeSessions] = useState(false);
  const [notifyOrgAdmins, setNotifyOrgAdmins] = useState(false);

  const isDeactivating = deactivateStatus === STATUS.LOADING;
  const isPreviewLoading = previewStatus === STATUS.LOADING || previewStatus === STATUS.IDLE;
  // Only trust preview counts that belong to the org this dialog is showing.
  const preview =
    deactivationPreview?.organizationId === organization?.id ? deactivationPreview : null;

  // The sub-org checkbox sets the scope of the other three actions (org alone vs
  // whole subtree), so the counts shown must follow it.
  const userCount = cascadeSubOrgs ? preview?.userCount : preview?.directUserCount;
  const adminCount = cascadeSubOrgs ? preview?.orgAdminCount : preview?.directOrgAdminCount;

  // Load fresh cascade counts and reset the checkboxes each time the dialog opens.
  useEffect(() => {
    if (open && organization) {
      dispatch(resetDeactivationState());
      setCascadeSubOrgs(true);
      setCascadeUsers(true);
      setRevokeSessions(false);
      setNotifyOrgAdmins(false);
      dispatch(fetchDeactivationPreviewAsync(organization.id));
    }
  }, [open, organization, dispatch]);

  // A server-side rejection (e.g. email not configured) refers to the options as
  // they were; once the admin changes any option the message is stale.
  const updateOption = (setter: (value: boolean) => void) => (value: boolean) => {
    setter(value);
    if (deactivateError) dispatch(clearDeactivateError());
  };

  const handleConfirm = async () => {
    if (!organization || isDeactivating) return;
    const result = await dispatch(
      deactivateOrganizationAsync({
        orgId: organization.id,
        body: { cascadeSubOrgs, cascadeUsers, revokeSessions, notifyOrgAdmins },
      })
    );
    if (!deactivateOrganizationAsync.fulfilled.match(result)) {
      // deactivateError (400 email-not-configured / 403 self-lockout guard / 404)
      // is rendered inline; keep the dialog open so the admin can adjust and retry.
      return;
    }
    const outcome = result.payload as DeactivateResult;
    // An already-deactivated org is a no-op; the API treats it as success and so do we.
    if (outcome.alreadyDeactivated) {
      showSuccessToast("Organization deactivated", `${organization.name} was already deactivated.`);
    } else {
      const summary = [
        pluralize(outcome.deactivatedOrganizations, "organization", "organizations"),
        pluralize(outcome.deactivatedUsers, "user", "users"),
      ];
      if (revokeSessions) summary.push(`${outcome.revokedSessions} sessions revoked`);
      if (notifyOrgAdmins) summary.push(`${outcome.notifiedAdmins} admins notified`);
      showSuccessToast("Organization deactivated", `Deactivated ${summary.join(", ")}.`);
    }
    onClose();
    onDeactivated();
  };

  return (
    // Ignore backdrop/Escape while the request is in flight so the dialog cannot
    // vanish mid-deactivation and leave the user unsure whether it went through.
    <Dialog open={open} onClose={isDeactivating ? () => undefined : onClose}>
      <div className="flex items-start justify-between gap-4">
        <span className="flex h-12 w-12 items-center justify-center rounded-lg border border-shadowborder bg-white shadow-light-sm">
          <BuildingOffice2Icon className="h-6 w-6 text-texthead" aria-hidden="true" />
        </span>
        <button
          type="button"
          onClick={onClose}
          disabled={isDeactivating}
          aria-label="Close"
          className="shrink-0 rounded-md p-1 text-zinc-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <DialogTitle className="mt-4">Deactivate &ldquo;{organization?.name}&rdquo;?</DialogTitle>
      <DialogDescription>
        This will mark the organization as deactivated. It will no longer appear as active in
        hierarchy views. Choose any additional actions to apply:
      </DialogDescription>
      <DialogBody>
        {isPreviewLoading ? (
          <div className="flex items-center gap-3 py-2 text-sm text-textColor">
            <ButtonSpinner />
            Loading affected counts...
          </div>
        ) : (
          <>
            {previewError && (
              <p className="mb-4 text-sm text-amber-600">
                Could not load the affected sub-organization and user counts.
              </p>
            )}
            <CheckboxGroup>
              <CheckboxField>
                <Checkbox
                  color="green"
                  checked={cascadeSubOrgs}
                  onChange={updateOption(setCascadeSubOrgs)}
                  disabled={isDeactivating}
                />
                <Label>
                  Deactivate all sub-organizations
                  <CountHint count={preview?.subOrgCount} noun="sub-orgs" />
                </Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  color="green"
                  checked={cascadeUsers}
                  onChange={updateOption(setCascadeUsers)}
                  disabled={isDeactivating}
                />
                <Label>
                  Deactivate all users
                  <CountHint count={userCount} noun="users" />
                </Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  color="green"
                  checked={revokeSessions}
                  onChange={updateOption(setRevokeSessions)}
                  disabled={isDeactivating}
                />
                <Label>Revoke all active sessions</Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  color="green"
                  checked={notifyOrgAdmins}
                  onChange={updateOption(setNotifyOrgAdmins)}
                  disabled={isDeactivating}
                />
                <Label>
                  Send notification email to org admins
                  <CountHint count={adminCount} noun="admins" />
                </Label>
              </CheckboxField>
            </CheckboxGroup>
          </>
        )}
        {deactivateError && (
          <p className="mt-4 text-sm font-medium text-red-500">{deactivateError}</p>
        )}
      </DialogBody>
      <DialogActions>
        <Button outline onClick={onClose} disabled={isDeactivating}>
          Cancel
        </Button>
        <Button color="red" onClick={handleConfirm} disabled={isDeactivating}>
          {isDeactivating && <ButtonSpinner />}
          {isDeactivating ? "Deactivating..." : "Deactivate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeactivateOrgDialog;
